import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import ServiceBox from './ServiceBox';
import { ServiceItem } from '../types';
import { useLanguageStore } from '../store/useLanguageStore';
import { allServices } from '../data/services';
import { API_ENDPOINTS } from '../config/api';

const ServiceGrid: React.FC = () => {
  const { t } = useLanguageStore();
  const navigate = useNavigate();
  const [services, setServices] = useState<ServiceItem[]>(allServices);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState<string | null>(null);

  // Fetch services from API
  useEffect(() => {
    const fetchServices = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      try {
        const response = await fetch(API_ENDPOINTS.units, {
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        if (data.success) {
          setServices(data.data);
        } else {
          throw new Error('API returned unsuccessful response');
        }
      } catch (err: any) {
        clearTimeout(timeoutId);
        // Only log if it's not a connection error (backend not running)
        if (err.name !== 'AbortError' && !err.message?.includes('Failed to fetch')) {
          console.warn('Error fetching services:', err);
        }
        // Silently fallback to local data
        setServices(allServices);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const handleServiceClick = (service: ServiceItem) => {
    navigate(`/units/${service.id}`);
  };

  return (
    <section className="py-12 md:py-16 bg-warm-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 sri-pattern opacity-30" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-8 md:mb-12 lg:mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-text-dark mb-4 md:mb-6">
            {t('our-units', 'Units', 'ඒකක')}
          </h2>
          <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-primary-green to-secondary-blue mx-auto mb-4 md:mb-6" />
          <p className="text-base sm:text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed px-4">
            {t(
              'units-description',
              'Comprehensive healthcare units designed to meet the diverse needs of our community with excellence and compassion.',
              'විශිෂ්ටත්වය සහ කරුණාව සහිතව අපගේ ප්‍රජාවේ විවිධ අවශ්‍යතා සපුරාලීම සඳහා නිර්මාණය කරන ලද සම්පූර්ණ සෞඛ්‍ය ඒකක.'
            )}
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12 lg:mb-16">
          {loading ? (
            <div className="col-span-full text-center py-12">
              <p className="text-gray-500">{t('loading', 'Loading...', 'පූරණය වෙමින්...')}</p>
            </div>
          ) : (
            services.map((service, index) => (
              <ServiceBox
                key={service.id}
                {...service}
                index={index}
                onClick={() => handleServiceClick(service)}
              />
            ))
          )}
        </div>

        {/* CTA Section */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-gradient-to-r from-primary-green to-secondary-blue rounded-2xl p-8 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              {t('need-immediate-care', 'Need Immediate Care?', 'ක්ෂණික සත්කාර අවශ්‍යද?')}
            </h3>
            <p className="text-white/90 mb-6 text-lg">
              {t(
                'emergency-message',
                'Our emergency department is open 24/7 to provide immediate medical attention.',
                'ක්ෂණික වෛද්‍ය ප්‍රතිකාර සැපයීම සඳහා අපගේ හදිසි දෙපාර්තමේන්තුව 24/7 විවෘතව පවතී.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="tel:+94472246260"
                className="btn-emergency"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('call-emergency', 'Call Emergency', 'හදිසි ඇමතුම')}
              </motion.a>
              <motion.button
                className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/20 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('find-directions', 'Get Directions', 'දිශානති ලබා ගන්න')}
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceGrid;
